import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AppRole, defaultDashboardFor } from '@/config/roles';
import { authPathWithNext } from '@/lib/auth/paths';
import { usePlatformSettings } from '@/hooks/usePlatformSettings';
import { SportsLoader } from '@/components/ui/sports-loader';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: AppRole[];
}

export const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const { user, roles, activeRole, loading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [slowSession, setSlowSession] = useState(false);

  useEffect(() => {
    if (!loading) {
      setChecking(false);
      setSlowSession(false);
      return;
    }
    const timer = window.setTimeout(() => setSlowSession(true), 8000);
    return () => window.clearTimeout(timer);
  }, [loading]);

  if (loading || checking) {
    return (
      <SportsLoader
        variant="fullscreen"
        message={slowSession ? "Still restoring your session…" : "Loading..."}
      />
    );
  }

  if (!user) {
    return <Navigate to={authPathWithNext(window.location.pathname, window.location.search)} replace />;
  }

  // Super admin only routes
  const superAdminOnlyRoutes = [
    '/super/admin-management',
    '/super/roles-permissions',
    '/super/ai-control',
    '/super/financial',
    '/super/audit-logs',
    '/super/system-settings',
    '/super/page-validator',
    '/super/feature-flags',
    '/super/monitoring',
    '/super/content/translations'
  ];

  const currentPath = window.location.pathname;
  const isSuperAdminOnlyRoute = superAdminOnlyRoutes.some(route => currentPath.startsWith(route));

  if (isSuperAdminOnlyRoute && !roles.includes('super_admin')) {
    // Non-super-admins cannot access super admin routes
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const hasRequiredRole = allowedRoles.some(role => roles.includes(role));
    
    if (!hasRequiredRole) {
      const to = defaultDashboardFor(roles, activeRole);
      return <Navigate to={to} replace />;
    }
  }

  return <>{children}</>;
};
